import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from 'constants/apiConfig';
import { notesKeys } from 'hooks/notes/notesHooks';
import type { Note } from 'hooks/notes/notesModel';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import {
  focFeedbackNoteBodyHtml,
  parseCapturedFeedback,
  upsertCapturedFeedbackLine,
} from 'components/MapPage/focFeedbackNoteBody';
import { focFeedbackNoteTitle } from 'components/MapPage/focTourFeedback';
import {
  registerEnsureFocFeedbackNote,
  registerOpenNotesFeedback,
  registerUpsertFocStepFeedback,
} from 'components/MapPage/focTourFeedbackBridge';
import {
  getFocFeedbackNoteId,
  hydrateFocStepFeedback,
  setFocFeedbackNoteId,
} from 'components/MapPage/focStepFeedbackSession';

import type { AiPanelMode } from 'components/DisasterGPT/chatV2/AiPanelToggle';

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

type ComposerSendHandler = (message: string) => boolean | void;

const ChatSidebarContext = createContext<{
  isVisible: boolean;
  show: () => void;
  hide: () => void;
  toggle: () => void;
  aiPickMode: boolean;
  setAiPickMode: (next: boolean) => void;
  aiPanel: AiPanelMode;
  setAiPanel: (panel: AiPanelMode) => void;
  /** FOC feedback creates org notes; keep Notes panel on org until drawer closes. */
  notesFeedbackActive: boolean;
  queuedOpenNoteId: string | null;
  consumeQueuedOpenNoteId: () => void;
  openNotesFeedback: (tourLabel: string) => Promise<void>;
  /** One-shot prompt stashed for the open composer. Cleared after consume. */
  queuedComposerPrompt: string | null;
  /** Item a pending ICS-202 draft will update. Cleared on send or close. */
  composerDraftLabel: string | null;
  queueComposerPrompt: (prompt: string, item?: string) => void;
  consumeQueuedComposerPrompt: () => void;
  registerComposerSendHandler: (handler: ComposerSendHandler | null) => void;
  /** True when a form draft consumed the send and the agent must not run. */
  notifyComposerSend: (message: string) => boolean;
}>({
  isVisible: false,
  show: noop,
  hide: noop,
  toggle: noop,
  aiPickMode: false,
  setAiPickMode: noop,
  aiPanel: 'ai',
  setAiPanel: noop,
  notesFeedbackActive: false,
  queuedOpenNoteId: null,
  consumeQueuedOpenNoteId: noop,
  openNotesFeedback: async () => undefined,
  queuedComposerPrompt: null,
  composerDraftLabel: null,
  queueComposerPrompt: noop,
  consumeQueuedComposerPrompt: noop,
  registerComposerSendHandler: noop,
  notifyComposerSend: () => false,
});

function pickWritableFeedbackNote(
  notes: Note[],
  title: string
): Note | undefined {
  return notes
    .filter((note) => note.title === title && note.isMine)
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )[0];
}

const ChatSidebarProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const groupGuid = useAppSelector(selectGroupGuid);
  const [isVisible, setIsVisible] = useState(false);
  const [aiPickMode, setAiPickMode] = useState(false);
  const [aiPanel, setAiPanel] = useState<AiPanelMode>('ai');
  const [notesFeedbackActive, setNotesFeedbackActive] = useState(false);
  const [queuedOpenNoteId, setQueuedOpenNoteId] = useState<string | null>(null);
  const [queuedComposerPrompt, setQueuedComposerPrompt] = useState<
    string | null
  >(null);
  const [composerDraftLabel, setComposerDraftLabel] = useState<string | null>(
    null
  );

  const show = useCallback(() => {
    setIsVisible(true);
  }, []);

  const hide = useCallback(() => {
    setIsVisible(false);
    setAiPickMode(false);
    setAiPanel('ai');
    setNotesFeedbackActive(false);
    setQueuedOpenNoteId(null);
    setQueuedComposerPrompt(null);
    setComposerDraftLabel(null);
  }, []);

  const toggle = useCallback(() => {
    setIsVisible((visible) => {
      if (visible) {
        setComposerDraftLabel(null);
        setAiPanel('ai');
        setNotesFeedbackActive(false);
        setQueuedOpenNoteId(null);
      }
      return !visible;
    });
  }, []);

  const consumeQueuedOpenNoteId = useCallback(() => {
    setQueuedOpenNoteId(null);
  }, []);

  const ensureFocFeedbackNote = useCallback(
    async (tourLabel: string): Promise<string | null> => {
      if (!groupGuid) {
        toast.error('Select an organization before opening FOC feedback notes.');
        return null;
      }
      const title = focFeedbackNoteTitle(tourLabel);
      const listKey = notesKeys.list({ kind: 'org', groupGuid });
      try {
        const cachedId = getFocFeedbackNoteId(tourLabel);
        if (cachedId) {
          const cachedList = queryClient.getQueryData<Note[]>(listKey) ?? [];
          const cached = cachedList.find((n) => n.id === cachedId);
          if (cached) return cached.id;
        }

        let list = queryClient.getQueryData<Note[]>(listKey);
        if (!list) {
          const response = await axios.get<Note[]>(config.apiGateway.notes, {
            params: { groupGuid },
          });
          list = response.data ?? [];
          queryClient.setQueryData(listKey, list);
        }

        const existing = pickWritableFeedbackNote(list, title);
        if (existing) {
          setFocFeedbackNoteId(tourLabel, existing.id);
          hydrateFocStepFeedback(
            tourLabel,
            parseCapturedFeedback(existing.body ?? '')
          );
          return existing.id;
        }

        const created = await axios.post<Note>(config.apiGateway.notes, {
          groupGuid,
          title,
          body: focFeedbackNoteBodyHtml(tourLabel),
          shared: true,
        });
        const note = created.data;
        setFocFeedbackNoteId(tourLabel, note.id);
        queryClient.setQueryData<Note[]>(listKey, (prev) => {
          const prevList = prev ?? [];
          if (prevList.some((entry) => entry.id === note.id)) return prevList;
          return [note, ...prevList];
        });
        void queryClient.invalidateQueries({ queryKey: listKey });
        return note.id;
      } catch (error) {
        const message =
          (error as { response?: { data?: { error?: string } } })?.response
            ?.data?.error || 'Could not create FOC feedback note.';
        toast.error(
          typeof message === 'string'
            ? message
            : 'Could not create FOC feedback note.'
        );
        return null;
      }
    },
    [groupGuid, queryClient]
  );

  const upsertFocStepFeedback = useCallback(
    async (input: {
      tourLabel: string;
      requirementKey: string;
      reaction: string;
      text: string;
    }) => {
      if (!groupGuid) return;
      const noteId = await ensureFocFeedbackNote(input.tourLabel);
      if (!noteId) return;
      const listKey = notesKeys.list({ kind: 'org', groupGuid });
      try {
        const list = queryClient.getQueryData<Note[]>(listKey) ?? [];
        const current =
          list.find((n) => n.id === noteId) ??
          (
            await axios.get<Note[]>(config.apiGateway.notes, {
              params: { groupGuid },
            })
          ).data?.find((n) => n.id === noteId);
        const prevBody = current?.body ?? focFeedbackNoteBodyHtml(input.tourLabel);
        const nextBody = upsertCapturedFeedbackLine(prevBody, {
          requirementKey: input.requirementKey,
          reaction: input.reaction,
          text: input.text,
        });
        if (nextBody === prevBody) return;
        const response = await axios.put<Note>(
          `${config.apiGateway.notes}/${noteId}`,
          { body: nextBody }
        );
        const note = response.data;
        queryClient.setQueryData<Note[]>(listKey, (prev) => {
          const prevList = prev ?? [];
          const idx = prevList.findIndex((entry) => entry.id === note.id);
          if (idx < 0) return [note, ...prevList];
          const copy = [...prevList];
          copy[idx] = note;
          return copy;
        });
      } catch (error) {
        const message =
          (error as { response?: { data?: { error?: string } } })?.response
            ?.data?.error || 'Could not save FOC step feedback.';
        toast.error(
          typeof message === 'string'
            ? message
            : 'Could not save FOC step feedback.'
        );
      }
    },
    [ensureFocFeedbackNote, groupGuid, queryClient]
  );

  const openNotesFeedback = useCallback(
    async (tourLabel: string) => {
      const noteId = await ensureFocFeedbackNote(tourLabel);
      if (!noteId) return;
      setNotesFeedbackActive(true);
      setAiPanel('notes');
      setQueuedOpenNoteId(noteId);
      setIsVisible(true);
    },
    [ensureFocFeedbackNote]
  );

  const openNotesFeedbackRef = useRef(openNotesFeedback);
  openNotesFeedbackRef.current = openNotesFeedback;
  const ensureFocFeedbackNoteRef = useRef(ensureFocFeedbackNote);
  ensureFocFeedbackNoteRef.current = ensureFocFeedbackNote;
  const upsertFocStepFeedbackRef = useRef(upsertFocStepFeedback);
  upsertFocStepFeedbackRef.current = upsertFocStepFeedback;

  useEffect(
    () =>
      registerOpenNotesFeedback((tourLabel) =>
        openNotesFeedbackRef.current(tourLabel)
      ),
    []
  );
  useEffect(
    () =>
      registerEnsureFocFeedbackNote((tourLabel) =>
        ensureFocFeedbackNoteRef.current(tourLabel)
      ),
    []
  );
  useEffect(
    () =>
      registerUpsertFocStepFeedback((input) =>
        upsertFocStepFeedbackRef.current(input)
      ),
    []
  );

  const queueComposerPrompt = useCallback((prompt: string, item?: string) => {
    setQueuedComposerPrompt(prompt);
    setComposerDraftLabel(item?.trim() ? item.trim() : null);
    setAiPanel('ai');
    setNotesFeedbackActive(false);
    setQueuedOpenNoteId(null);
    setIsVisible(true);
  }, []);

  const consumeQueuedComposerPrompt = useCallback(() => {
    setQueuedComposerPrompt(null);
  }, []);

  const composerSendHandlerRef = useRef<ComposerSendHandler | null>(null);
  const registerComposerSendHandler = useCallback(
    (handler: ComposerSendHandler | null) => {
      composerSendHandlerRef.current = handler;
    },
    []
  );
  const notifyComposerSend = useCallback((message: string) => {
    const consumed = composerSendHandlerRef.current?.(message) === true;
    if (consumed) setComposerDraftLabel(null);
    return consumed;
  }, []);

  useEffect(() => {
    document.body.classList.toggle('ai-workspace-pick-mode', aiPickMode);
    return () => {
      document.body.classList.remove('ai-workspace-pick-mode');
    };
  }, [aiPickMode]);

  return (
    <ChatSidebarContext.Provider
      value={{
        isVisible,
        show,
        hide,
        toggle,
        aiPickMode,
        setAiPickMode,
        aiPanel,
        setAiPanel,
        notesFeedbackActive,
        queuedOpenNoteId,
        consumeQueuedOpenNoteId,
        openNotesFeedback,
        queuedComposerPrompt,
        composerDraftLabel,
        queueComposerPrompt,
        consumeQueuedComposerPrompt,
        registerComposerSendHandler,
        notifyComposerSend,
      }}
    >
      {children}
    </ChatSidebarContext.Provider>
  );
};

export default ChatSidebarProvider;
export const useChatSidebar = () => useContext(ChatSidebarContext);
